import { CountrySelect, StateSelect } from '@/views/screens/common';
import { Col, Row } from 'antd';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { calculateCube, generateStopNumbers } from '../../../../../../../utilz';
import { routeFileSelector } from '../../../../../../../utilz/i18n';
import FilterCard from '../../../../../../components/Cards/FilterCard';
import FormCheckBox from '../../../../../../components/Inputs/FormCheckBox';
import FormInput from '../../../../../../components/Inputs/FormInput';
import FormSelectBox from '../../../../../../components/Inputs/FormSelectBox';
import HiddenFormInput from '../../../../../../components/Inputs/HiddenFormInput';
import FormLabel from '../../../../../../components/Labels/FormLabel';
import HalPortNameSelector from '../../../../../../components/Selectors/HalPortNameSelector';
import PortTypeSelector from '../../../../../../components/Selectors/PortTypeSelector';
import PostalCodeInput from '../../../../../../components/Selectors/PostalCodeInput';
import StopTypeSelector from '../../../../../../components/Selectors/StopTypeSelector';
import SaveCancelButtons from '../../../SaveCancelButtons';

const TMSTransportationDetailsCard = ({
  index,
  name,
  remove,
  stopDetails = {},
  handleDataChange,
  handleSubmit,
  defaultValues = {},
  isLast = false,
  form,
  totalStops = 0,
}) => {
  let { editMode, isNew, stopTypeID } = stopDetails;
  editMode = Boolean(editMode);

  const { pathname: activeTab } = useLocation();
  const namespace = useMemo(() => routeFileSelector(activeTab), [activeTab]);
  const { t } = useTranslation([namespace, 'translation']);

  const setEditMode = (val) => {
    handleDataChange({ editMode: val }, index);
  };

  const handleSave = () => {
    const data = { ...stopDetails, editMode: false, isNew: false };
    handleSubmit(data, index);
  };

  const handleCancel = () => {
    if (isNew) {
      remove(name);
    } else {
      handleDataChange({ ...defaultValues, editMode: false }, index);
    }
  };

  const cube = useMemo(
    () =>
      calculateCube(
        stopDetails?.length,
        stopDetails?.width,
        stopDetails?.height,
        stopDetails?.billQty,
        stopDetails?.dimUom,
      ),
    [stopDetails?.length, stopDetails?.width, stopDetails?.height, stopDetails?.billQty, stopDetails?.dimUom],
  );

  useEffect(() => {
    if (cube) {
      handleDataChange({ cube }, index);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cube]);

  const handleZipDetails = (data) => {
    if (data?.zip) {
      handleDataChange(
        {
          state: data?.state,
          city: data?.city,
          postalCode: data?.zip,
          countryCode: data?.countryCode,
        },
        index,
      );
    } else {
      handleDataChange(
        {
          state: undefined,
          city: undefined,
        },
        index,
      );
    }

    if (data?.zip) {
      handleDataChange({ isValidLocation: true }, index);
    } else {
      handleDataChange({ isValidLocation: false }, index);
    }
    form.validateFields();
  };

  useEffect(() => {
    handleDataChange({ isValidLocation: true }, index);
  }, [stopDetails?.postalCode]);

  const handlePortDetails = (data) => {
    handleDataChange(
      {
        portName: data?.portName,
        portRecKey: data?.portRecKey,
        portCode: data?.portCode,
        portType: data?.portType,
      },
      index,
    );
  };

  const handlePortTypeChange = () => {
    handleDataChange(
      {
        portRecKey: undefined,
        portCode: undefined,
        portName: undefined,
      },
      index,
    );
  };

  return (
    <FilterCard
      shadow={false}
      showBorder
      className={isLast === false ? 'mb-15' : ''}
      customHeader={
        <div className="w-100">
          <Row justify="space-between" align="middle">
            <Col flex={1}>
              <Row gutter={15} align="middle">
                <Col>
                  <FormCheckBox
                    name={[name, 'selected']}
                    checkBoxClass="normal-checkbox"
                    className="m-0"
                    label={false}
                  />
                </Col>
                <Col sm={6}>
                  <StopTypeSelector
                    autoFocus={editMode}
                    name={[name, 'stopTypeID']}
                    checkBoxClass="normal-checkbox"
                    className="m-0"
                    label={
                      <FormLabel className="f-12 fw-400">
                        {t('translation:getQuote.transportationDetails.stopType.label')}
                      </FormLabel>
                    }
                    singleLineMode
                    colon
                    editMode={editMode}
                    margin="m-0"
                    rules={[
                      {
                        required: true,
                        message: t('translation:getQuote.transportationDetails.stopType.message'),
                      },
                    ]}
                    hideDelivery={totalStops === 1}
                  />
                </Col>
                <Col sm={6}>
                  <FormSelectBox
                    label={t('translation:getQuote.transportationDetails.stopNo.label')}
                    editMode={editMode && stopTypeID > 0}
                    singleLineMode
                    colon
                    name={[name, 'stopSequence']}
                    placeholder={t('translation:getQuote.transportationDetails.stopNo.placeholder')}
                    margin="m-0"
                    options={generateStopNumbers(totalStops, stopDetails?.stopTypeID)}
                    rules={[
                      {
                        required: true,
                        message: t('translation:getQuote.transportationDetails.stopNo.message'),
                      },
                    ]}
                  />
                </Col>
              </Row>
            </Col>
            <Col>
              <SaveCancelButtons
                onSave={handleSave}
                editMode={editMode}
                handleRemove={() => remove(name)}
                setEditMode={setEditMode}
                onCancel={handleCancel}
                saveButtonType="button"
              />
            </Col>
          </Row>
        </div>
      }
    >
      <div className="p-15">
        <Row gutter={20}>
          <Col sm={4}>
            <CountrySelect
              label={t('translation:default.fields.country.label')}
              name={[name, 'countryCode']}
              editMode={editMode}
              margin="m-0"
            />
          </Col>
          <Col sm={4}>
            <PostalCodeInput
              name={[name, 'postalCode']}
              label={t('translation:default.fields.postalCode.label')}
              margin="m-0"
              editMode={editMode}
              countryCode={stopDetails?.countryCode}
              rules={[
                {
                  required: true,
                  message: t('translation:default.fields.postalCode.message'),
                },
                () => ({
                  validator() {
                    if (stopDetails?.isValidLocation === false) {
                      return Promise.reject(t('translation:default.fields.postalCode.invalid'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              handleZipDetails={handleZipDetails}
              maxLength={10}
            />
          </Col>
          <Col sm={4}>
            <StateSelect
              name={[name, 'state']}
              label={t('translation:default.fields.stateProvince.label')}
              margin="m-0"
              editMode={editMode}
              countryCode={stopDetails?.countryCode}
            />
          </Col>
          <Col sm={4}>
            <FormInput
              name={[name, 'city']}
              label={t('translation:default.fields.city.label')}
              margin="m-0"
              editMode={editMode}
            />
          </Col>
          <Col sm={4}>
            <PortTypeSelector
              name={[name, 'portType']}
              label={t('translation:default.fields.portType.label')}
              margin="m-0"
              editMode={editMode}
              onChange={handlePortTypeChange}
            />
          </Col>
          <Col sm={4}>
            <HalPortNameSelector
              margin="m-0"
              name={[name, 'portName']}
              editMode={editMode}
              portType={stopDetails?.portType}
              portCode={''}
              stateCode={stopDetails?.state}
              countryCode={stopDetails?.countryCode}
              zip={stopDetails?.postalCode}
              handlePortDetails={handlePortDetails}
            />
          </Col>
        </Row>
      </div>
      <HiddenFormInput name={[name, 'editMode']} />
      <HiddenFormInput name={[name, 'isNew']} />
      <HiddenFormInput name={[name, 'gridOperation']} />
      <HiddenFormInput name={[name, 'isValidLocation']} />
      <HiddenFormInput name={[name, 'portRecKey']} />
      <HiddenFormInput name={[name, 'portCode']} />
    </FilterCard>
  );
};

export default TMSTransportationDetailsCard;
